<template>
<div>
    <form-errors :errors="formErrors" v-if="!hideFormErrors" />

    <div v-if="!formData.taxConfigs || formData.taxConfigs.length === 0">
        <p class="text-muted">No applicable taxes found for this employee's work location and address.</p>
    </div>

    <div v-for="taxConfig in formData.taxConfigs" :key="taxConfig.unique_tax_id" class="mb-4">
        <h3 class="mb-2">{{ taxConfig.sym_tax.description }}</h3>

        <div class="form-row" v-if="taxConfig.is_eligible_for_non_resident_exemption && taxConfig.unique_tax_id !== '00-000-0000-FIT-000'">
            <div class="col-12 mb-2">
                <alert-box v-if="taxConfig.has_non_res_certificate" type="warning" class="mt-2">
                    Employee is eligible for a non-resident exemption for this tax. There must be a non-resident certificate on file for this employee. If you do not have a certificate on file, check the box below.
                </alert-box>
                <div class="form-check">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'non_resident_' + taxConfig.unique_tax_id"
                        :checked="!taxConfig.has_non_res_certificate"
                        @change="taxConfig.has_non_res_certificate = !$event.target.checked"
                    />
                    <label class="form-check-label" :for="'non_resident_' + taxConfig.unique_tax_id">Employee does not have a non-resident certificate</label>
                </div>
            </div>
        </div>

        <div v-if="!taxConfig.has_non_res_certificate" class="form-row">
            <div class="col-12 mb-2">
                <div class="form-check">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'is_exempt_' + taxConfig.unique_tax_id"
                        v-model="taxConfig.is_exempt"
                    />
                    <label class="form-check-label" :for="'is_exempt_' + taxConfig.unique_tax_id">Exempt from withholding</label>
                </div>
            </div>
        </div>

        <div v-if="!taxConfig.is_exempt && !taxConfig.has_non_res_certificate" class="form-row">
            <div v-for="param in visibleParameters(taxConfig)" :key="param.parameter_name" class="col-12 mb-2">
                <template v-if="param.regex_type === 'PickList'">
                    <label :for="taxConfig.unique_tax_id + '_' + param.parameter_name">{{ param.certificate_line_no }} — {{ param.description }}</label>
                    <div>
                        <select
                            class="form-control w-auto"
                            :id="taxConfig.unique_tax_id + '_' + param.parameter_name"
                            v-model="taxConfig.tax_data[param.parameter_name]"
                        >
                            <option value="">--Default--</option>
                            <option v-for="[val, label] in param.list_values_parsed" :key="val" :value="val">{{ label }}</option>
                        </select>
                    </div>
                    <small v-if="param.help_text" class="form-text text-muted">{{ param.help_text }}</small>
                </template>
                <template v-else-if="param.data_type === 'Boolean'">
                    <label :for="taxConfig.unique_tax_id + '_' + param.parameter_name">{{ param.certificate_line_no }} — {{ param.description }}</label>
                    <div>
                        <select
                            class="form-control w-auto"
                            :id="taxConfig.unique_tax_id + '_' + param.parameter_name"
                            v-model="taxConfig.tax_data[param.parameter_name]"
                        >
                            <option value="">--Default--</option>
                            <option :value="true">Yes</option>
                            <option :value="false">No</option>
                        </select>
                    </div>
                    <small v-if="param.help_text" class="form-text text-muted">{{ param.help_text }}</small>
                </template>
                <template v-else>
                    <label :for="taxConfig.unique_tax_id + '_' + param.parameter_name">{{ param.certificate_line_no }} — {{ param.description }}</label>
                    <input
                        class="form-control"
                        style="max-width: 160px"
                        :id="taxConfig.unique_tax_id + '_' + param.parameter_name"
                        :type="param.data_type === 'Integer' || param.data_type === 'Dollar' ? 'number' : 'text'"
                        :step="param.data_type === 'Dollar' ? '0.01' : '1'"
                        v-model="taxConfig.tax_data[param.parameter_name]"
                        :placeholder="param.default_value"
                    />
                    <small v-if="param.help_text" class="form-text text-muted">{{ param.help_text }}</small>
                </template>
            </div>
            <div v-if="taxConfig.sym_tax.allows_additional_wh" class="col-12 mb-2">
                <label :for="'additional_wh_' + taxConfig.unique_tax_id">Additional Withholding Amount</label>
                <input
                    class="form-control"
                    style="max-width: 160px"
                    type="number"
                    step="0.01"
                    min="0"
                    max="10000"
                    :id="'additional_wh_' + taxConfig.unique_tax_id"
                    v-model="taxConfig.additional_wh"
                    placeholder="0.00"
                />
            </div>
        </div>
    </div>
</div>
</template>

<script>
import api from '@/api'
import FormMixin from '@/mixins/Form'
import FormErrors from '@/components/FormErrors'
import AlertBox from '@/components/AlertBox'

export default {
    mixins: [FormMixin],
    components: { FormErrors, AlertBox },
    props: {
        client: { required: true },
        employerHire: { required: true },
        hideFormErrors: { required: false, default: false },
    },
    data() {
        return {
            formData: {
                taxConfigs: [],
            },
        }
    },
    mounted() {
        this.$store.dispatch('START_LOADING')
        api.get(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}/sym-tax-config`).then((resp) => {
            this.$store.dispatch('STOP_LOADING')
            this.formData.taxConfigs = this.makeTaxConfigData(resp)
        }).catch((errors) => {
            this.$store.dispatch('STOP_LOADING')
            this.$emit('form_errors', errors.__all__ || ['Failed to load tax withholding configuration.'])
        })
    },
    methods: {
        makeTaxConfigData(rawConfigs) {
            return (rawConfigs || []).map(cfg => {
                const taxData = Object.assign({}, cfg.tax_data)
                // Coerce Boolean fields to proper JS boolean
                for (const param of cfg.parameters) {
                    if (param.data_type === 'Boolean' && param.parameter_name in taxData) {
                        const v = taxData[param.parameter_name]
                        taxData[param.parameter_name] = (v === true || v === 'true' || v === 'True')
                            ? true
                            : (v === false || v === 'false' || v === 'False') ? false : ''
                    }
                }
                return {
                    unique_tax_id: cfg.unique_tax_id,
                    sym_tax: cfg.sym_tax,
                    parameters: cfg.parameters,
                    tax_data: taxData,
                    is_exempt: cfg.is_exempt,
                    has_non_res_certificate: cfg.has_non_res_certificate !== undefined ? cfg.has_non_res_certificate : false,
                    is_eligible_for_non_resident_exemption: cfg.is_eligible_for_non_resident_exemption || false,
                    additional_wh: cfg.additional_wh != null ? cfg.additional_wh : null,
                }
            })
        },
        visibleParameters(taxConfig) {
            return taxConfig.parameters
                .filter(p => p.certificate_line_no)
                .slice()
                .sort((a, b) => a.certificate_line_no.localeCompare(b.certificate_line_no))
        },
        onSubmit() {
            // Build the sym_tax_config dict keyed by unique_tax_id
            const symTaxConfig = {}
            for (const cfg of this.formData.taxConfigs) {
                symTaxConfig[cfg.unique_tax_id] = {
                    tax_data: cfg.tax_data,
                    is_exempt: cfg.is_exempt,
                    has_non_res_certificate: cfg.has_non_res_certificate,
                    additional_wh: cfg.additional_wh || null,
                }
            }
            this.$emit('updated', { sym_tax_config: symTaxConfig })
        },
    },
}
</script>
